import React, { useEffect, useMemo, useState } from "react";
import {
  FaBalanceScale,
  FaCookieBite,
  FaDatabase,
  FaEnvelope,
  FaExternalLinkAlt,
  FaGavel,
  FaGlobe,
  FaIdCard,
  FaLock,
  FaShieldAlt,
  FaTrophy,
  FaUserShield,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScrollToTop";
import { getSettings } from "../../services/settingsServices";

const APP_LOGO = "/logo_monapplitournoi_512.png";

const DEFAULT_SETTINGS = {
  siteweb_club: "",
  nom_club: "",
  adresse_club: "",
  responsable_club: "",
  mail_club: "",
  tel_club: "",
  siret_club: "",
};

const fallback = (value, defaultValue = "Non renseigné") => {
  const cleaned = String(value || "").trim();
  return cleaned || defaultValue;
};

const InfoCard = ({ icon: Icon, title, children }) => {
  return (
    <article className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300">
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-950 dark:text-white">
            {title}
          </h2>

          <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            {children}
          </div>
        </div>
      </div>
    </article>
  );
};

const DataBadge = ({ children }) => {
  return (
    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200">
      {children}
    </span>
  );
};

const MentionsLegalesPage = () => {
  useScrollToTop();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    let cancelled = false;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();

        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setSettings({
            ...DEFAULT_SETTINGS,
            ...data[0],
          });
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };

    fetchSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  const currentYear = new Date().getFullYear();

  const siteUrl = useMemo(() => {
    return fallback(settings.siteweb_club, "MonAppliTournoi");
  }, [settings.siteweb_club]);

  const contactEmail = fallback(settings.mail_club, "monappliclub@gmail.com");

  return (
    <main className="min-h-screen bg-slate-50 pt-[120px] text-slate-950 dark:bg-slate-950 dark:text-white">
      <section className="relative overflow-hidden px-4 pb-16 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute -left-24 top-20 h-80 w-80 rounded-full bg-blue-400/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-80 h-80 w-80 rounded-full bg-orange-400/15 blur-3xl" />

        <div className="relative mx-auto max-w-[1180px]">
          <header className="overflow-hidden rounded-[2.25rem] bg-slate-950 text-white shadow-2xl shadow-slate-950/20">
            <div className="relative p-6 sm:p-8 lg:p-10">
              <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-orange-500/25 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-24 -right-20 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-3xl">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-200">
                    <FaBalanceScale className="h-3.5 w-3.5" />
                    Textes réglementaires
                  </div>

                  <h1 className="mt-5 text-3xl font-bold tracking-tight sm:text-5xl">
                    Mentions légales & information RGPD
                  </h1>

                  <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">
                    Cette page présente les informations légales relatives à
                    MonAppliTournoi, application web de gestion de tournois
                    interclubs de Judo / Jujitsu : préparation des compétitions,
                    import des compétiteurs, pesées, répartitions, scoreboards,
                    résultats publics et statistiques.
                  </p>
                </div>

                <div className="flex justify-center lg:justify-end">
                  <div className="rounded-[2rem] border border-white/10 bg-white/10 p-4 backdrop-blur">
                    <img
                      src={APP_LOGO}
                      alt="Logo MonAppliTournoi"
                      className="h-28 w-28 rounded-[1.75rem] object-contain sm:h-32 sm:w-32"
                    />
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="mt-8 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <InfoCard icon={FaIdCard} title="1. Éditeur du site">
              <p>
                Le site et l’application web <strong>MonAppliTournoi</strong>{" "}
                sont édités pour la présentation et l’exploitation d’une solution
                de gestion de tournois sportifs.
              </p>

              <ul className="space-y-2">
                <li>
                  <strong>Nom du site :</strong> MonAppliTournoi
                </li>
                <li>
                  <strong>Site web :</strong> {siteUrl}
                </li>
                <li>
                  <strong>Responsable de publication :</strong>{" "}
                  {fallback(settings.responsable_club, "Laurent VICTORIN")}
                </li>
                <li>
                  <strong>Contact :</strong>{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="font-bold text-orange-600 underline underline-offset-4 dark:text-orange-300"
                  >
                    {contactEmail}
                  </a>
                </li>
                <li>
                  <strong>Téléphone :</strong>{" "}
                  {fallback(settings.tel_club, "06 80 72 77 07")}
                </li>
                <li>
                  <strong>SIRET :</strong>{" "}
                  {fallback(settings.siret_club, "À compléter si applicable")}
                </li>
              </ul>
            </InfoCard>

            <InfoCard icon={FaGlobe} title="2. Hébergement et réalisation">
              <p>
                Le site et les services associés sont hébergés par des
                prestataires techniques permettant la mise en ligne de
                l’application, de son API et de sa base de données.
              </p>

              <ul className="space-y-2">
                <li>
                  <strong>Hébergeur principal :</strong> Hostinger International
                  Ltd.
                </li>
                <li>
                  <strong>Site hébergeur :</strong>{" "}
                  <a
                    href="https://www.hostinger.fr/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-bold text-orange-600 underline underline-offset-4 dark:text-orange-300"
                  >
                    hostinger.fr <FaExternalLinkAlt className="h-3 w-3" />
                  </a>
                </li>
                <li>
                  <strong>Réalisation / maintenance :</strong> Laurent VICTORIN
                  — MonAppliClub
                </li>
              </ul>
            </InfoCard>
          </div>

          <div className="mt-5 grid gap-5">
            <InfoCard icon={FaTrophy} title="3. Objet du service MonAppliTournoi">
              <p>
                MonAppliTournoi est une application web destinée à faciliter
                l’organisation de tournois interclubs et compétitions de Judo /
                Jujitsu. Elle permet notamment :
              </p>

              <div className="flex flex-wrap gap-2">
                <DataBadge>Création de compétitions</DataBadge>
                <DataBadge>Import de compétiteurs</DataBadge>
                <DataBadge>Formulaires clubs invités</DataBadge>
                <DataBadge>Pesées & présences</DataBadge>
                <DataBadge>Poules et tableaux</DataBadge>
                <DataBadge>Répartition sur tapis</DataBadge>
                <DataBadge>Scoreboards</DataBadge>
                <DataBadge>Résultats publics</DataBadge>
                <DataBadge>Statistiques</DataBadge>
              </div>

              <p>
                Les informations publiées sur le site sont fournies à titre
                indicatif. Malgré le soin apporté à leur mise à jour, des erreurs
                ou omissions peuvent subsister. L’éditeur ne saurait être tenu
                responsable d’une interprétation ou d’une utilisation inadaptée
                des informations diffusées.
              </p>
            </InfoCard>

            <InfoCard icon={FaDatabase} title="4. Données personnelles traitées">
              <p>
                Dans le cadre du fonctionnement de l’application, certaines
                données personnelles peuvent être collectées ou traitées. Elles
                dépendent des fonctionnalités utilisées par l’organisateur de la
                compétition.
              </p>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/50">
                  <h3 className="font-bold text-slate-950 dark:text-white">
                    Comptes utilisateurs
                  </h3>
                  <p className="mt-2 text-sm">
                    Nom, prénom, adresse email, rôle, identifiants de connexion,
                    historiques d’accès ou d’actions nécessaires au service.
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/50">
                  <h3 className="font-bold text-slate-950 dark:text-white">
                    Compétiteurs
                  </h3>
                  <p className="mt-2 text-sm">
                    Nom, prénom, club, catégorie, sexe, âge ou année de
                    naissance, poids, grade, numéro de licence si utilisé, statut
                    de présence ou de pesée.
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/50">
                  <h3 className="font-bold text-slate-950 dark:text-white">
                    Résultats sportifs
                  </h3>
                  <p className="mt-2 text-sm">
                    Combats, victoires, défaites, classements, podiums,
                    résultats publics, statistiques et informations liées au
                    déroulement sportif.
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/50">
                  <h3 className="font-bold text-slate-950 dark:text-white">
                    Clubs invités
                  </h3>
                  <p className="mt-2 text-sm">
                    Coordonnées des responsables, inscriptions transmises,
                    échanges liés à l’organisation du tournoi.
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/50">
                  <h3 className="font-bold text-slate-950 dark:text-white">
                    Contact & assistance
                  </h3>
                  <p className="mt-2 text-sm">
                    Nom, email, téléphone si indiqué dans le message, contenu de
                    la demande et informations nécessaires à la réponse.
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/50">
                  <h3 className="font-bold text-slate-950 dark:text-white">
                    Données techniques
                  </h3>
                  <p className="mt-2 text-sm">
                    Logs techniques, adresse IP, navigateur, date de connexion,
                    statistiques de visite et informations de sécurité.
                  </p>
                </div>
              </div>

              <p>
                Les données relatives aux mineurs doivent être utilisées avec
                une vigilance particulière. Les organisateurs sont invités à ne
                collecter que les informations strictement nécessaires à la
                gestion sportive et administrative de la compétition.
              </p>
            </InfoCard>

            <InfoCard icon={FaUserShield} title="5. Finalités et base légale">
              <p>
                Les données sont traitées pour permettre le bon fonctionnement de
                l’application et la gestion des compétitions. Les principales
                finalités sont :
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>création et administration des comptes utilisateurs ;</li>
                <li>gestion des droits d’accès selon les rôles ;</li>
                <li>préparation, organisation et suivi des compétitions ;</li>
                <li>importation et répartition des compétiteurs ;</li>
                <li>édition des poules, tableaux, feuilles de route et podiums ;</li>
                <li>affichage des scoreboards et résultats publics ;</li>
                <li>réponse aux messages envoyés via le formulaire de contact ;</li>
                <li>sécurisation de l’application et prévention des abus ;</li>
                <li>mesure de fréquentation et amélioration du service.</li>
              </ul>

              <p>
                Selon les cas, les traitements peuvent être fondés sur
                l’exécution d’un service demandé, l’intérêt légitime de
                l’éditeur ou de l’organisateur, le respect d’une obligation
                légale, ou le consentement lorsque celui-ci est requis.
              </p>
            </InfoCard>

            <InfoCard icon={FaLock} title="6. Accès, confidentialité et sécurité">
              <p>
                L’accès aux espaces d’administration est réservé aux utilisateurs
                autorisés. Les rôles permettent de limiter l’accès aux
                informations selon les besoins : administrateur, organisateur,
                client, table d’arbitrage, coach, visiteur ou utilisateur de
                démonstration.
              </p>

              <p>
                Les données ne sont pas vendues à des tiers. Elles peuvent être
                accessibles uniquement aux personnes ayant besoin d’y accéder
                pour l’organisation de la compétition, le support technique ou la
                maintenance de l’application.
              </p>

              <p>
                Des mesures techniques raisonnables sont mises en œuvre :
                authentification, séparation des rôles, accès restreint aux
                espaces sensibles, sauvegardes, hébergement sécurisé et limitation
                des droits selon le profil utilisateur.
              </p>
            </InfoCard>

            <InfoCard icon={FaShieldAlt} title="7. Durée de conservation">
              <p>
                Les données sont conservées pendant une durée proportionnée à
                leur finalité. À titre indicatif :
              </p>

              <ul className="list-disc space-y-2 pl-5">
                <li>
                  les comptes utilisateurs sont conservés tant que le compte est
                  actif ou nécessaire à la gestion du service ;
                </li>
                <li>
                  les données de compétition peuvent être conservées pour
                  permettre l’historique sportif, les résultats publics et les
                  statistiques ;
                </li>
                <li>
                  les messages de contact sont conservés le temps nécessaire au
                  traitement de la demande ;
                </li>
                <li>
                  les logs techniques peuvent être conservés pour des raisons de
                  sécurité, de maintenance ou de preuve ;
                </li>
                <li>
                  les données de paiement, lorsqu’un paiement est réalisé via un
                  prestataire externe, sont traitées selon les règles du
                  prestataire de paiement concerné.
                </li>
              </ul>

              <p>
                Une demande de suppression ou d’anonymisation peut être adressée
                au contact indiqué sur cette page, sous réserve des obligations de
                conservation applicables.
              </p>
            </InfoCard>

            <InfoCard icon={FaGavel} title="8. Droits des personnes concernées">
              <p>
                Conformément à la réglementation applicable en matière de données
                personnelles, les personnes concernées peuvent demander l’accès,
                la rectification, l’effacement, la limitation ou l’opposition au
                traitement de leurs données, dans les conditions prévues par la
                réglementation.
              </p>

              <p>
                Pour exercer ces droits, une demande peut être adressée à :
              </p>

              <p>
                <a
                  href={`mailto:${contactEmail}`}
                  className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-4 py-2 font-bold text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-600"
                >
                  <FaEnvelope className="h-4 w-4" />
                  {contactEmail}
                </a>
              </p>

              <p>
                La demande doit permettre d’identifier la personne concernée et
                les données visées. Une vérification d’identité peut être
                demandée en cas de doute raisonnable.
              </p>
            </InfoCard>

            <InfoCard icon={FaCookieBite} title="9. Cookies et traceurs">
              <p>
                Le site peut utiliser des cookies ou traceurs nécessaires à son
                fonctionnement : authentification, sécurité, préférences
                d’affichage, maintien de session ou mesure technique de
                fréquentation.
              </p>

              <p>
                Certains traceurs peuvent être exemptés de consentement lorsqu’ils
                sont strictement nécessaires au service demandé. Les traceurs non
                nécessaires, notamment publicitaires ou de suivi étendu, doivent
                faire l’objet d’une information et, lorsque la réglementation
                l’impose, d’un consentement préalable.
              </p>

              <p>
                L’utilisateur peut configurer son navigateur pour limiter ou
                refuser certains cookies. Le refus de cookies nécessaires peut
                toutefois empêcher l’accès à certaines fonctionnalités de
                l’application.
              </p>
            </InfoCard>

            <InfoCard icon={FaGavel} title="10. Propriété intellectuelle">
              <p>
                Les textes, interfaces, graphismes, logos, éléments visuels,
                bases de données, code applicatif et fonctionnalités de
                MonAppliTournoi sont protégés par les règles relatives à la
                propriété intellectuelle, sauf mention contraire.
              </p>

              <p>
                Toute reproduction, extraction, adaptation, modification,
                diffusion ou exploitation non autorisée de tout ou partie du
                service est interdite sans accord préalable écrit.
              </p>
            </InfoCard>

            <InfoCard icon={FaBalanceScale} title="11. Responsabilité">
              <p>
                L’éditeur s’efforce d’assurer le bon fonctionnement de
                l’application. Toutefois, la responsabilité de l’éditeur ne
                saurait être engagée en cas d’interruption temporaire, erreur de
                saisie par l’utilisateur, mauvaise configuration, perte de
                connexion, indisponibilité d’un service tiers ou utilisation non
                conforme de la plateforme.
              </p>

              <p>
                Les organisateurs restent responsables de la vérification des
                informations saisies, des règles sportives appliquées, des listes
                de compétiteurs importées et de l’affichage public des résultats.
              </p>
            </InfoCard>
          </div>

          <footer className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-5 text-sm text-slate-600 shadow-sm dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
            <p>
              Dernière mise à jour : {currentYear}. Cette page constitue une base
              d’information générale et peut être complétée par une politique de
              confidentialité, des conditions générales d’utilisation et, le cas
              échéant, des conditions de vente.
            </p>
          </footer>
        </div>
      </section>
    </main>
  );
};

export default MentionsLegalesPage;